import { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// ── Styled components ──────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1.2rem;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(140, 74, 47, 0.15);
  border-radius: 50px;
  backdrop-filter: blur(6px);
`;

const PlayButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.wood};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: scale(1.07);
  }

  svg {
    width: 16px;
    height: 16px;
    fill: #fff;
    /* nudge play icon optically */
    margin-left: ${({ $playing }) => ($playing ? '0' : '2px')};
  }
`;

const ProgressArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
`;

const TimeLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.gray};
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TrackBar = styled.div`
  flex: 1;
  position: relative;
  height: 4px;
  background: rgba(140, 74, 47, 0.15);
  border-radius: 4px;
  cursor: pointer;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.wood};
  width: ${({ $pct }) => $pct}%;
  transition: width 0.1s linear;
  pointer-events: none;
`;

const Thumb = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $pct }) => $pct}%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.wood};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(s) {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AudioPlayer({ src, title }) {
  const audioRef = useRef(null);
  const barRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hover, setHover] = useState(false);

  // Sync time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrent(audio.currentTime);
    const onLoad = () => setDuration(audio.duration);
    const onEnd = () => setPlaying(false);

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoad);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoad);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying(p => !p);
  }, [playing]);

  const seek = useCallback((e) => {
    const bar = barRef.current;
    const audio = audioRef.current;
    if (!bar || !audio || !duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * duration;
  }, [duration]);

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <>
      <audio ref={audioRef} src={src} preload="metadata" />
      <Wrapper>
        <PlayButton onClick={togglePlay} $playing={playing} aria-label={playing ? 'Pause' : 'Lecture'}>
          {playing ? (
            // Pause icon
            <svg viewBox="0 0 24 24"><rect x="5" y="3" width="4" height="18" /><rect x="15" y="3" width="4" height="18" /></svg>
          ) : (
            // Play icon
            <svg viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" /></svg>
          )}
        </PlayButton>

        <ProgressArea>
          <TimeLabel>{fmt(current)}</TimeLabel>
          <TrackBar
            ref={barRef}
            onClick={seek}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <Fill $pct={pct} />
            <Thumb $pct={pct} $visible={hover} />
          </TrackBar>
          <TimeLabel>{fmt(duration)}</TimeLabel>
        </ProgressArea>
      </Wrapper>
    </>
  );
}
